/**
 * Billing v2 — Plan Validation
 *
 * Server-side validation for plan CRUD.
 * Mirrors DB constraints from billingv2_schema.sql.
 */

/** Fields allowed in PATCH updates (whitelist) */
// No stripe_price_id: Stripe sync owns it.
export const UPDATABLE_PLAN_FIELDS = new Set([
  'display_name', 'price_monthly_cents', 'billing_channel',
  'expiry_days',
  'grace_period_days', 'initial_balance_microusd', 'payment_to_balance_microusd', 'payment_to_balance_count', 'plan_credits_expire', 'min_topup_cents',
  'text_cost_multiplier', 'text_min_charge_microusd', 'text_rounding_decimals',
  'voice_cost_multiplier', 'voice_min_charge_microusd', 'voice_rounding_decimals',
  'embedding_min_charge_microusd', 'embedding_rounding_decimals',
  'voice_included', 'voice_trial_enabled', 'voice_trial_duration_hours', 'voice_trial_budget_microusd',
  'voice_concurrency_limit', 'voice_agents_limit', 'campaigns_enabled', 'campaigns_limit', 'campaigns_trial_enabled', 'pbx_numbers_limit', 'pbx_flows_limit',
  'knowledge_bases_limit', 'knowledge_bases_size_mb_limit', 'users_limit',
  'crm_integrations_limit', 'shopify_stores_limit', 'marketing_channels_limit',
  'daily_spending_limit_microusd', 'monthly_spending_limit_microusd',
  'topup_bonus_percent', 'features', 'benefits', 'billing_intervals',
  'status', 'sort_order', 'shopify_insights_trial_duration_hours', 'shopify_plan_handle',
])

/**
 * Validate plan fields against DB constraints.
 * Returns error message string or null if valid.
 */
export function validatePlanFields(body: Record<string, unknown>): string | null {
  // All numeric fields that must be validated — collect for type check
  const allNumericFields = new Set([
    'price_monthly_cents', 'initial_balance_microusd', 'min_topup_cents',
    'text_min_charge_microusd', 'voice_min_charge_microusd', 'embedding_min_charge_microusd',
    'voice_concurrency_limit', 'voice_agents_limit', 'campaigns_limit', 'pbx_numbers_limit', 'pbx_flows_limit',
    'crm_integrations_limit', 'shopify_stores_limit', 'topup_bonus_percent', 'grace_period_days',
    'sort_order',
    'text_cost_multiplier', 'voice_cost_multiplier',
    'text_rounding_decimals', 'voice_rounding_decimals', 'embedding_rounding_decimals',
    'daily_spending_limit_microusd', 'monthly_spending_limit_microusd',
    'campaigns_limit', 'knowledge_bases_limit', 'knowledge_bases_size_mb_limit', 'users_limit', 'marketing_channels_limit',
    'voice_trial_duration_hours', 'voice_trial_budget_microusd', 'shopify_insights_trial_duration_hours',
    'expiry_days',
  ])

  // Type check: reject non-number, non-null values for numeric fields
  for (const key of allNumericFields) {
    if (key in body && body[key] !== null && typeof body[key] !== 'number') {
      return `${key} must be a number`
    }
  }

  const booleanFields = new Set([
    'voice_included',
    'voice_trial_enabled',
    'campaigns_enabled',
    'campaigns_trial_enabled',
    'plan_credits_expire',
  ])
  for (const key of booleanFields) {
    if (key in body && typeof body[key] !== 'boolean') {
      return `${key} must be a boolean`
    }
  }

  // Integer fields: must be non-negative whole numbers (DB type INTEGER)
  const nonNegativeInts: [string, string][] = [
    ['price_monthly_cents', 'Price'],
    ['initial_balance_microusd', 'Initial balance'],
    ['min_topup_cents', 'Min topup'],
    ['text_min_charge_microusd', 'Text min charge'],
    ['voice_min_charge_microusd', 'Voice min charge'],
    ['embedding_min_charge_microusd', 'Embedding min charge'],
    ['voice_concurrency_limit', 'Voice concurrency'],
    ['voice_agents_limit', 'Voice agents limit'],
    ['topup_bonus_percent', 'Topup bonus'],
    ['grace_period_days', 'Grace period'],
    ['sort_order', 'Sort order'],
  ]
  for (const [key, label] of nonNegativeInts) {
    if (key in body && typeof body[key] === 'number') {
      const v = body[key] as number
      if (!Number.isInteger(v)) return `${label} must be an integer`
      if (v < 0) return `${label} cannot be negative`
    }
  }

  // DB constraint: text_cost_multiplier >= 1.0 AND voice_cost_multiplier >= 1.0
  const multipliers: [string, string][] = [
    ['text_cost_multiplier', 'Text multiplier'],
    ['voice_cost_multiplier', 'Voice multiplier'],
  ]
  for (const [key, label] of multipliers) {
    if (!(key in body) || typeof body[key] !== 'number') continue
    const value = body[key] as number
    if (value < 1.0) return `${label} must be >= 1.0`
    // numeric(6,2) would round silently.
    if (Math.round(value * 100) / 100 !== value) return `${label} must have at most 2 decimal places`
  }

  const roundingKeys = ['text_rounding_decimals', 'voice_rounding_decimals', 'embedding_rounding_decimals']
  for (const key of roundingKeys) {
    if (key in body && typeof body[key] === 'number') {
      const v = body[key] as number
      if (!Number.isInteger(v)) return `${key} must be an integer`
      if (v < 0 || v > 6) return `${key} must be 0-6`
    }
  }

  // Nullable INTEGER fields: null is allowed, but if present must be non-negative integer
  const nullableNonNeg: string[] = [
    'daily_spending_limit_microusd', 'monthly_spending_limit_microusd',
    'campaigns_limit', 'knowledge_bases_limit', 'knowledge_bases_size_mb_limit', 'users_limit', 'marketing_channels_limit',
    'pbx_numbers_limit', 'pbx_flows_limit', 'crm_integrations_limit', 'shopify_stores_limit',
    'voice_trial_duration_hours', 'voice_trial_budget_microusd',
    'shopify_insights_trial_duration_hours',
    'expiry_days',
  ]
  for (const key of nullableNonNeg) {
    if (key in body && body[key] !== null && typeof body[key] === 'number') {
      const v = body[key] as number
      if (!Number.isInteger(v)) return `${key} must be an integer`
      if (v < 0) return `${key} cannot be negative`
    }
  }

  if (
    'expiry_days' in body &&
    body.expiry_days !== null &&
    typeof body.expiry_days === 'number' &&
    body.expiry_days < 1
  ) {
    return 'expiry_days must be null or a positive integer'
  }

  // Fixed grants are independent of the plan price and can exceed it.
  if ('payment_to_balance_microusd' in body) {
    const amount = body.payment_to_balance_microusd
    if (typeof amount !== 'number' || !Number.isSafeInteger(amount) || amount < 0) {
      return 'payment_to_balance_microusd must be a non-negative safe integer'
    }
  }

  if ('payment_to_balance_count' in body && body.payment_to_balance_count !== null) {
    const count = body.payment_to_balance_count
    if (typeof count !== 'number' || !Number.isSafeInteger(count) || count < 1) {
      return 'payment_to_balance_count must be null (all payments) or a positive integer'
    }
  }

  // shopify_insights_trial_duration_hours: null = included without a time limit,
  // positive = trial hours; 0 is ambiguous. See `lib/billing/services/shopify-insights-access.ts`.
  if ('shopify_insights_trial_duration_hours' in body && body.shopify_insights_trial_duration_hours === 0) {
    return 'shopify_insights_trial_duration_hours must be null (unlimited grant) or positive (trial duration). 0 is not allowed.'
  }

  if ('billing_channel' in body && !['stripe', 'shopify'].includes(body.billing_channel as string)) {
    return 'billing_channel must be stripe or shopify'
  }

  if ('status' in body && !['draft', 'internal', 'published', 'archived'].includes(body.status as string)) {
    return 'status must be draft, internal, published, or archived'
  }

  return null
}

/**
 * Pick only whitelisted fields from body for PATCH.
 */
export function pickUpdatableFields(body: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {}
  for (const key of Object.keys(body)) {
    if (UPDATABLE_PLAN_FIELDS.has(key)) {
      result[key] = body[key]
    }
  }
  return result
}
